/**
 * 手動報價單明細純函式（#82）：主項目可計價、子項目只描述不計價；最多兩層。
 * 表單主項目：{ key, lineId, productNo, itemName, qty, unitName, unitPrice, customerRemark, internalRemark, subLines[] }
 * 表單子項目：{ key, subLineId, productNo, itemName, qty, unitName, customerRemark, internalRemark }
 * qty／unitPrice 為字串；空字串 → 送 null（草稿可不完整）。
 */

const MAX_DECIMALS = 4;
const MAX_INTEGER_DIGITS = 16;

let keySeq = 0;

function nextKey(prefix) {
  keySeq += 1;
  return `${prefix}-${Date.now().toString(36)}-${keySeq}`;
}

function isBlank(value) {
  return value === '' || value === null || value === undefined;
}

function toText(value) {
  return isBlank(value) ? '' : String(value);
}

/** 小數位數（科學記號以實際值計）。 */
export function countDecimals(value) {
  if (isBlank(value)) {
    return 0;
  }
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return 0;
  }
  const text = String(value).includes('e') ? number.toFixed(10) : String(value);
  const fraction = text.split('.')[1] ?? '';
  return fraction.replace(/0+$/, '').length;
}

export function hasAtMostFourDecimals(value) {
  return countDecimals(value) <= MAX_DECIMALS;
}

/** 後端：最多 4 位小數、16 位整數。 */
export function fitsQuotationDecimal(value) {
  if (isBlank(value)) {
    return true;
  }
  const integerPart = String(Math.trunc(Math.abs(Number(value))));
  return hasAtMostFourDecimals(value) && integerPart.length <= MAX_INTEGER_DIGITS;
}

export function createQuotationSubLine(subLine = {}) {
  return {
    key: subLine.subLineId || nextKey('sub'),
    subLineId: subLine.subLineId ?? null,
    productNo: toText(subLine.productNo),
    itemName: toText(subLine.itemName),
    qty: toText(subLine.qty),
    unitName: toText(subLine.unitName),
    customerRemark: toText(subLine.customerRemark),
    internalRemark: toText(subLine.internalRemark),
  };
}

export function createQuotationLine(line = {}) {
  return {
    key: line.lineId || nextKey('line'),
    lineId: line.lineId ?? null,
    productNo: toText(line.productNo),
    itemName: toText(line.itemName),
    qty: toText(line.qty),
    unitName: toText(line.unitName),
    unitPrice: toText(line.unitPrice),
    customerRemark: toText(line.customerRemark),
    internalRemark: toText(line.internalRemark),
    subLines: (line.subLines ?? []).map(createQuotationSubLine),
  };
}

export function mapQuotationDetailToFormValues(detail) {
  return {
    customerId: detail?.customer?.id ?? '',
    contactName: toText(detail?.contactName),
    contactPhone: toText(detail?.contactPhone),
    customerAddress: toText(detail?.customerAddress),
    paymentTermId: detail?.paymentTermId ?? '',
    roundingMode: detail?.roundingMode ?? 'HALF_UP',
    remark: toText(detail?.remark),
    lines: (detail?.lines ?? []).map(createQuotationLine),
  };
}

function trimOrNull(value) {
  const text = String(value ?? '').trim();
  return text ? text : null;
}

function numberOrNull(value) {
  return isBlank(value) ? null : Number(value);
}

function toApiSubLine(subLine) {
  const item = {
    productNo: trimOrNull(subLine.productNo),
    itemName: trimOrNull(subLine.itemName),
    qty: numberOrNull(subLine.qty),
    unitName: trimOrNull(subLine.unitName),
    customerRemark: trimOrNull(subLine.customerRemark),
    internalRemark: trimOrNull(subLine.internalRemark),
  };
  return subLine.subLineId ? { subLineId: subLine.subLineId, ...item } : item;
}

function toApiLine(line) {
  const item = {
    productNo: trimOrNull(line.productNo),
    itemName: trimOrNull(line.itemName),
    qty: numberOrNull(line.qty),
    unitName: trimOrNull(line.unitName),
    unitPrice: numberOrNull(line.unitPrice),
    customerRemark: trimOrNull(line.customerRemark),
    internalRemark: trimOrNull(line.internalRemark),
    subLines: (line.subLines ?? []).map(toApiSubLine),
  };
  return line.lineId ? { lineId: line.lineId, ...item } : item;
}

/** 表頭欄位一律明確送出（含 null），避免更換客戶時漏送聯絡欄位被後端清空。 */
function toApiHeader(values) {
  return {
    customerId: values.customerId,
    contactName: trimOrNull(values.contactName),
    contactPhone: trimOrNull(values.contactPhone),
    customerAddress: trimOrNull(values.customerAddress),
    paymentTermId: values.paymentTermId || null,
    roundingMode: values.roundingMode || 'HALF_UP',
    remark: trimOrNull(values.remark),
  };
}

/** create body：新列不帶 ID。 */
export function buildQuotationCreateBody(values) {
  return {
    ...toApiHeader(values),
    lines: (values.lines ?? []).map((line) => toApiLine({
      ...line,
      lineId: null,
      subLines: (line.subLines ?? []).map((subLine) => ({ ...subLine, subLineId: null })),
    })),
  };
}

/**
 * PATCH body：`lines[]` 整份替換。保留的主／子項目帶回原 `lineId`／`subLineId`，未帶回者後端刪除。
 * @param {object} values 表單值
 * @param {string} updatedAt detail `record.updatedAt`（原樣送回）
 */
export function buildQuotationPatchBody(values, updatedAt) {
  return {
    updatedAt,
    ...toApiHeader(values),
    lines: (values.lines ?? []).map(toApiLine),
  };
}

/**
 * Customer detail → 報價單聯絡文字快照：預設聯絡人（姓名、手機或電話）＋預設帳單地址。
 * @param {object} customer camelCase Customer detail
 */
export function pickQuotationContactSnapshot(customer) {
  const contacts = customer?.contacts ?? [];
  const contact = contacts.find((row) => row?.isDefault) ?? contacts[0] ?? null;
  const addresses = (customer?.addresses ?? []).filter((row) => {
    const type = String(row?.addressType ?? '').toUpperCase();
    return type === 'BILLING' || type === 'BOTH';
  });
  const address = addresses.find((row) => row?.isDefault) ?? addresses[0]
    ?? (customer?.addresses ?? [])[0] ?? null;
  return {
    contactName: toText(contact?.name),
    contactPhone: toText(contact?.mobile || contact?.phone),
    customerAddress: address ? [address.postalCode, address.address].filter(Boolean).join(' ') : '',
  };
}

/**
 * ISSUED／EXPIRED 只可改內部備註：單列（主或子）internal_remark 的局部 PATCH body。
 * @param {string} updatedAt
 * @param {{ lineId: string, subLineId?: string, internalRemark: string|null }} target
 */
export function buildQuotationInternalRemarkPatch(updatedAt, { lineId, subLineId, internalRemark }) {
  const remark = trimOrNull(internalRemark);
  const lineUpdate = subLineId
    ? { lineId, subLineUpdates: [{ subLineId, internalRemark: remark }] }
    : { lineId, internalRemark: remark };
  return { updatedAt, lineUpdates: [lineUpdate] };
}
